import { useState, useEffect } from "react";
import { Link, useLocation as useRouterLocation } from "react-router-dom";
import { Menu, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/contexts/LocationContext";
import { getOtherLocation } from "@/lib/locations";
import dsEmblem from "@/assets/logos/ds-emblem.webp";

export function WarmHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const routerLocation = useRouterLocation();
  const { location, locationId, isSeattle } = useLocation();
  const otherLocation = getOtherLocation(locationId);

  const basePath = isSeattle ? "/seattle" : "";
  const navLinks = [
    { href: `${basePath}/`, label: "Home" },
    { href: `${basePath}/about`, label: "Our Story" },
    { href: `${basePath}/services`, label: "Care & Amenities" },
    { href: `${basePath}/gallery`, label: "Our Home" },
    { href: `${basePath}/testimonials`, label: "Family Stories" },
    { href: `${basePath}/blog`, label: "Resources" },
    { href: `${basePath}/contact`, label: "Visit Us" },
  ];

  // Only the homepage has a dark full-bleed video hero behind the header.
  const path = routerLocation.pathname;
  const isHome = path === "/" || path === "/seattle" || path === "/seattle/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [routerLocation.pathname]);

  // Transparent (white text) only at the very top of the homepage, over the video.
  const onDark = isHome && !scrolled && !mobileMenuOpen;

  const isActive = (href: string) => {
    const currentPath = routerLocation.pathname;
    if (href === basePath || href === `${basePath}/`) {
      return currentPath === "/" || currentPath === "/seattle" || currentPath === "/seattle/";
    }
    return currentPath === href || currentPath === `${href}/`;
  };

  return (
    <header
      className={`fixed top-0 z-50 w-full transition-colors duration-300 ${
        onDark
          ? "bg-transparent"
          : "border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      }`}
    >
      {/* Legibility scrim for white text over the bright top of the hero video. */}
      {onDark && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/35 to-transparent"
          aria-hidden="true"
        />
      )}

      <nav aria-label="Primary" className="container relative mx-auto flex h-16 items-center justify-between px-4 md:h-20">
        {/* Single brand anchor — the logo badge lives here (and only here on screen). */}
        <Link to={`${basePath}/`} className="flex items-center gap-2.5 shrink-0">
          <img
            src={dsEmblem}
            alt="Dawit Solomon Adult Family Home"
            className={`h-10 w-10 transition-transform hover:scale-105 md:h-11 md:w-11 ${
              onDark ? "brightness-0 invert" : ""
            }`}
          />
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-[0.95rem] font-semibold tracking-[-0.01em] md:text-base ${
                onDark ? "text-white" : "text-primary"
              }`}
            >
              Dawit Solomon
            </span>
            <span
              className={`mt-0.5 hidden text-[0.62rem] font-medium uppercase tracking-[0.16em] sm:block ${
                onDark ? "text-white/70" : "text-muted-foreground"
              }`}
            >
              Adult Family Home · {location.shortName}
            </span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                onDark
                  ? isActive(link.href)
                    ? "text-white"
                    : "text-white/75 hover:text-white"
                  : isActive(link.href)
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
              {isActive(link.href) && (
                <span
                  className={`absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full ${
                    onDark ? "bg-white" : "bg-accent"
                  }`}
                  aria-hidden="true"
                />
              )}
            </Link>
          ))}

          {/* Other location link */}
          <Link
            to={otherLocation.id === "seattle" ? "/seattle/" : "/"}
            className={`ml-3 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              onDark
                ? "border-white/30 text-white hover:bg-white/10"
                : "border-border text-foreground hover:border-accent/50 hover:text-accent-strong"
            }`}
          >
            <MapPin className={`h-3 w-3 ${onDark ? "text-white" : "text-accent-strong"}`} />
            {otherLocation.shortName}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            className={`rounded-full h-12 w-12 ${onDark ? "text-white hover:bg-white/10" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`border-t border-border/50 bg-background lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container mx-auto px-4 py-2">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className={`block rounded-lg px-4 py-4 text-base font-medium transition-colors hover:bg-accent/10 ${
                isActive(link.href)
                  ? "bg-accent/10 text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}

          <div className="mt-4 pt-4 border-t border-border/50">
            <Link
              to={otherLocation.id === "seattle" ? "/seattle/" : "/"}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 rounded-lg bg-accent/10 border border-accent/20 px-4 py-4 text-base font-semibold text-accent-strong hover:bg-accent/20 transition-colors"
            >
              <MapPin className="h-4 w-4" />
              Visit our {otherLocation.shortName} location
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
